import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Eye, Send, Clock, User, AlertCircle, ExternalLink, Languages, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale/tr";
import { API, BACKEND_URL } from "../lib/api";
import SEO, { ArticleSEO } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import Comments from "../components/Comments";
import { useLang } from "../context/LangContext";

const NewsDetail = () => {
  const { slug, id } = useParams();
  const identifier = slug || id;
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [translatedTitle, setTranslatedTitle] = useState(null);
  const [translatedSummary, setTranslatedSummary] = useState(null);
  const [translatedContent, setTranslatedContent] = useState(null);
  const [translating, setTranslating] = useState(false);
  const { lang } = useLang();

  useEffect(() => {
    fetchNewsDetail();
  }, [identifier]);

  const fetchNewsDetail = async () => {
    try {
      const response = await axios.get(`${API}/news/${identifier}`);
      setNews(response.data);
      
      const allNews = await axios.get(`${API}/news?category=${response.data.category}&limit=4`);
      setRelatedNews(allNews.data.filter(n => n.id !== response.data.id).slice(0, 3));
    } catch (e) {
      console.error("Error fetching news:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (news && lang !== "tr") {
      translateArticle();
    } else {
      setTranslatedTitle(null);
      setTranslatedSummary(null);
      setTranslatedContent(null);
    }
  }, [lang, news?.id]);

  const translateArticle = async () => {
    if (!news || lang === "tr") return;
    setTranslating(true);
    try {
      const [titleRes, summaryRes, contentRes] = await Promise.all([
        axios.post(`${API}/ai/translate`, { text: news.title, target_lang: lang }),
        axios.post(`${API}/ai/translate`, { text: news.summary, target_lang: lang }),
        axios.post(`${API}/ai/translate`, { text: news.content?.slice(0, 2000), target_lang: lang })
      ]);
      setTranslatedTitle(titleRes.data.translated);
      setTranslatedSummary(summaryRes.data.translated);
      setTranslatedContent(contentRes.data.translated);
    } catch { }
    finally { setTranslating(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <div className="animate-pulse text-amber-500">Yükleniyor...</div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-zinc-400">Haber bulunamadı.</p>
          <Link to="/" className="text-amber-500 hover:text-amber-400 mt-4 inline-block">
            Ana sayfaya dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b]" data-testid="news-detail-page">
      <ArticleSEO news={news} />
      
      {/* Hero Image */}
      <div className="relative h-[40vh] lg:h-[60vh]">
        <img 
          src={news.image_url} 
          alt={news.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/60 to-transparent" />
      </div>

      <article className="max-w-4xl mx-auto px-4 -mt-24 lg:-mt-32 relative z-10">
        {/* Mobile-friendly Breadcrumb */}
        <div className="hidden lg:block">
          <Breadcrumb items={[
            { label: "Ana Sayfa", href: "/" },
            { label: news.category, href: `/?category=${encodeURIComponent(news.category)}` },
            { label: news.title }
          ]} />
        </div>
        
        <span className="inline-block px-2.5 py-1 lg:px-3 bg-amber-500 text-black text-xs lg:text-sm font-bold rounded-full mb-3 lg:mb-4">
          {news.category}
        </span>
        
        <h1 className="text-2xl lg:text-5xl font-bold text-white mb-4 lg:mb-6 leading-tight">
          {translatedTitle || news.title}
        </h1>
        
        {translating && (
          <div className="flex items-center gap-2 mb-4 text-sm text-blue-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <Languages className="w-4 h-4" />
            Çevriliyor...
          </div>
        )}
        
        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-3 lg:gap-4 text-zinc-400 text-xs lg:text-sm mb-6 lg:mb-8 pb-6 lg:pb-8 border-b border-zinc-800">
          <span className="flex items-center gap-1.5 lg:gap-2">
            <User className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
            {news.author}
          </span>
          <span className="flex items-center gap-1.5 lg:gap-2">
            <Clock className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
            {format(new Date(news.created_at), "d MMM yyyy", { locale: tr })}
          </span>
          <span className="flex items-center gap-1.5 lg:gap-2">
            <Eye className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
            {news.views}
          </span>
          {news.telegram_sent && (
            <span className="flex items-center gap-1.5 text-blue-400">
              <Send className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">Telegram'a gönderildi</span>
            </span>
          )}
          {news.sentiment && (
            <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] lg:text-xs font-medium ${
              news.sentiment === 'positive' ? 'bg-emerald-500/10 text-emerald-400' :
              news.sentiment === 'negative' ? 'bg-red-500/10 text-red-400' :
              'bg-zinc-700/50 text-zinc-400'
            }`}>
              {news.sentiment === 'positive' ? 'Pozitif' : news.sentiment === 'negative' ? 'Negatif' : 'Nötr'}
            </span>
          )}
        </div>

        {/* AI Tags */}
        {news.ai_tags && news.ai_tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 lg:gap-2 mb-4 lg:mb-6 -mx-1">
            {news.ai_tags.map((tag, i) => (
              <Link 
                key={i} 
                to={`/entity/topic/${encodeURIComponent(tag)}`}
                className="px-2.5 py-1 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 text-[10px] lg:text-xs rounded-full transition-colors"
              >
                {tag}
              </Link>
            ))}
          </div>
        )}

        {/* AI Summary */}
        {news.ai_summary && (
          <div className="mb-4 lg:mb-6 p-3 lg:p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
            <p className="text-[10px] lg:text-xs text-blue-400 font-semibold uppercase tracking-wider mb-1.5 lg:mb-2">AI Özet</p>
            <p className="text-zinc-300 text-sm leading-relaxed">{news.ai_summary}</p>
          </div>
        )}

        {/* Entities: People & Companies */}
        {((news.people && news.people.length > 0) || (news.companies && news.companies.length > 0) || (news.entities && news.entities.length > 0)) && (
          <div className="mb-4 lg:mb-6 flex flex-wrap gap-3 lg:gap-4">
            {news.people && news.people.length > 0 && (
              <div>
                <p className="text-[10px] lg:text-xs text-zinc-500 mb-1.5">Kişiler</p>
                <div className="flex flex-wrap gap-1">
                  {news.people.map((p, i) => (
                    <Link 
                      key={i} 
                      to={`/entity/person/${encodeURIComponent(p)}`}
                      className="px-2 py-0.5 lg:px-2.5 lg:py-1 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 text-[10px] lg:text-xs rounded-lg transition-colors"
                    >
                      {p}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {news.companies && news.companies.length > 0 && (
              <div>
                <p className="text-[10px] lg:text-xs text-zinc-500 mb-1.5">Şirketler</p>
                <div className="flex flex-wrap gap-1">
                  {news.companies.map((c, i) => (
                    <Link 
                      key={i}
                      to={`/entity/company/${encodeURIComponent(c)}`}
                      className="px-2 py-0.5 lg:px-2.5 lg:py-1 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-[10px] lg:text-xs rounded-lg transition-colors"
                    >
                      {c}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {news.entities && news.entities.length > 0 && !news.people?.length && !news.companies?.length && (
              <div>
                <p className="text-[10px] lg:text-xs text-zinc-500 mb-1.5">Varlıklar</p>
                <div className="flex flex-wrap gap-1">
                  {news.entities.slice(0, 6).map((e, i) => (
                    <Link 
                      key={i}
                      to={`/entity/${e.type?.toLowerCase() || 'topic'}/${encodeURIComponent(e.name)}`}
                      className="px-2 py-0.5 lg:px-2.5 lg:py-1 bg-zinc-700/50 text-zinc-300 hover:bg-zinc-600/50 text-[10px] lg:text-xs rounded-lg transition-colors"
                    >
                      {e.name} <span className="text-zinc-500">({e.type})</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Article Content */}
        <div className="prose prose-invert prose-sm lg:prose-lg max-w-none">
          <p className="text-lg lg:text-xl text-zinc-300 leading-relaxed mb-4 lg:mb-6">
            {translatedSummary || news.summary}
          </p>
          <div className="text-zinc-300 text-sm lg:text-base leading-relaxed whitespace-pre-wrap">
            {translatedContent || news.content}
          </div>
        </div>

        {/* Social Share Buttons - Mobile Optimized */}
        <div className="mt-8 lg:mt-10 pt-6 lg:pt-8 border-t border-zinc-800">
          <h4 className="text-xs lg:text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3 lg:mb-4">Bu haberi paylaş</h4>
          <div className="flex flex-wrap gap-2 lg:gap-3">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(news.title + ' - ' + window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-2 lg:py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl transition-colors text-sm"
              data-testid="share-whatsapp"
            >
              <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              <span className="hidden lg:inline">WhatsApp</span>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(news.title)}&url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-2 lg:py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors text-sm"
              data-testid="share-twitter"
            >
              <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              <span className="hidden lg:inline">Twitter</span>
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-2 lg:py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-colors text-sm"
              data-testid="share-facebook"
            >
              <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              <span className="hidden lg:inline">Facebook</span>
            </a>
            <a
              href={`https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(news.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-2 lg:py-2.5 bg-sky-500 hover:bg-sky-400 text-white rounded-xl transition-colors text-sm"
              data-testid="share-telegram"
            >
              <Send className="w-4 h-4 lg:w-5 lg:h-5" />
              <span className="hidden lg:inline">Telegram</span>
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Link kopyalandı!');
              }}
              className="flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-2 lg:py-2.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded-xl transition-colors text-sm"
              data-testid="share-copy"
            >
              <ExternalLink className="w-4 h-4 lg:w-5 lg:h-5" />
              <span className="hidden lg:inline">Linki Kopyala</span>
            </button>
          </div>
        </div>

        {/* Related News - Mobile Optimized */}
        {relatedNews.length > 0 && (
          <div className="mt-10 lg:mt-16 pt-6 lg:pt-8 border-t border-zinc-800">
            <h3 className="text-lg lg:text-xl font-bold text-white mb-4 lg:mb-6">İlgili Haberler</h3>
            
            {/* Mobile: Horizontal Scroll */}
            <div className="lg:hidden -mx-4 px-4">
              <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 snap-x">
                {relatedNews.map((item) => (
                  <Link 
                    key={item.id}
                    to={`/haber/${item.slug || item.id}`}
                    className="flex-shrink-0 w-[180px] snap-start touch-feedback"
                  >
                    <div className="aspect-video rounded-xl overflow-hidden mb-2">
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <h4 className="text-xs font-medium text-white line-clamp-2 leading-snug">
                      {item.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>

            {/* Desktop: Grid */}
            <div className="hidden lg:grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedNews.map((item) => (
                <Link 
                  key={item.id}
                  to={`/haber/${item.slug || item.id}`}
                  className="group bg-zinc-900/50 rounded-lg overflow-hidden border border-zinc-800/50 hover:border-amber-500/30 transition-all"
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img 
                      src={item.image_url} 
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-medium text-white group-hover:text-amber-400 transition-colors line-clamp-2">
                      {item.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Comments */}
        <Comments newsId={news.id} />
      </article>

      {/* Bottom Spacing */}
      <div className="h-20 bg-[#0a0a0b]" />
    </div>
  );
};

export default NewsDetail;
